/**
 * Configuration constants for performance logging
 */

import { PerfEventType,ResourceMatcher } from './types';

/**
 * Performance event type constants
 * Use these instead of string literals for type safety
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '@thoughtspot/perf-log';
 * setPerfLog(PERF_KEYS.APP_INIT_START, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.APP_INIT_START, PERF_EVENT.COMPLETE);
 */
export const PERF_EVENT = {
    START: 'START' as PerfEventType,
    COMPLETE: 'COMPLETE' as PerfEventType,
};

/**
 * Hierarchy map: key -> parent path
 * Defines the nested structure of performance events
 */
export const PERF_PATH_MAP = {
    DOCUMENT_PARSE: '',
    APP_INIT_START: '',
    APP_PRELOAD_INITIATED: '',
    PRE_APP_RENDER_ASYNC: '',
    SESSION_INFO_UPDATE: 'PRE_APP_RENDER_ASYNC',
    INITIALIZE_APP_LOCALE_COMPLETE: 'PRE_APP_RENDER_ASYNC',
    PREAUTH_INFO_READ: 'PRE_APP_RENDER_ASYNC.SESSION_INFO_UPDATE',
    UPDATE_SERVICES: 'PRE_APP_RENDER_ASYNC.SESSION_INFO_UPDATE',
    INITIALISE_LOCALE_FORMATS: 'PRE_APP_RENDER_ASYNC.SESSION_INFO_UPDATE.UPDATE_SERVICES',
    APP_ROUTER: '',
    APP_ROUTER_LOADER: '',
    APP_ROOT: '',
    APP_VIEW: '',
    AUTHENTICATED_APP_LAYOUT: '',
    PINBOARD_PAGE_START: '',
    PINBOARD_PAGE_LAZY_IMPORT: '',
    LOAD_PINBOARD_SESSION_API: '',
    PINBOARD_CONTENT_CONTAINER: '',
    PINBOARD_VIZ_CONTAINER_VIEWPORT: '',
    ANSWER_CONTENT_CONTAINER: 'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    LOAD_CONTEXT_BOOK_API: 'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    VIZ_RENDER_QUEUE_PRIMARY: 'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    VIZ_RENDER_QUEUE_SECONDARY: 'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    DRAW_COMPONENT_INITIALIZATION:
        'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    CHART_RENDER_COMPLETE: 'PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId',
    CHART_VIZ_CONTAINER_LAZY_IMPORT: '',
    TABLE_VIZ_CONTAINER_LAZY_IMPORT: '',
    HEADLINE_VIZ_CONTAINER_LAZY_IMPORT: '',
    CHART_VIZ_CONTAINER_PRELOAD: '',
    TABLE_VIZ_CONTAINER_PRELOAD: '',
    HEADLINE_VIZ_CONTAINER_PRELOAD: '',
} as const;

/**
 * Performance log key constants
 * Use these with setPerfLog() to avoid string literals and get autocomplete
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 */
export const PERF_KEYS = Object.keys(PERF_PATH_MAP).reduce((acc, key) => {
    acc[key as keyof typeof PERF_PATH_MAP] = key as keyof typeof PERF_PATH_MAP;
    return acc;
}, {} as Record<keyof typeof PERF_PATH_MAP, keyof typeof PERF_PATH_MAP>);

/**
 * Resource patterns to capture from Performance API
 * Only the first occurrence of each resource will be captured
 */
export const RESOURCE_MATCHERS: ResourceMatcher[] = [
    { key: 'PREAUTH_INFO_API_NETWORK', pattern: 'preauth/info' },
    { key: 'MAIN_JS_DOWNLOAD_NETWORK', pattern: /main.*\.js/ },
    { key: 'NUMBERS_JSON_DOWNLOAD_NETWORK', pattern: 'number-format' },
    { key: 'STRINGS_JSON_DOWNLOAD_NETWORK', pattern: 'strings' },
    { key: 'DATE_JSON_DOWNLOAD_NETWORK', pattern: 'date-formats-en-us' },
    { key: 'LOAD_PINBOARD_API_NETWORK', pattern: 'op=LoadPinboardSession' },
    { key: 'LOAD_CONTEXT_BOOK_API_NETWORK', pattern: 'op=LoadContextBook' },
];

